import CTASection from "@/components/CTASection";
import {
  CATEGORY_BY_SLUG,
  WARDROBE_SYSTEM_DIAGRAMS,
  WARDROBE_INTERIOR_HIGHLIGHTS,
  WARDROBE_MATERIALS,
  WARDROBE_GALLERY,
} from "@/lib/siteData";

const WardrobePage = () => {
  const category = CATEGORY_BY_SLUG["wardrobe"];

  return (
    <div data-testid="wardrobe-page">
      {/* HERO */}
      <section className="relative">
        <div className="relative h-[80vh] min-h-[560px] overflow-hidden">
          <img
            src={category.hero}
            alt="Modular wardrobes — hinged and sliding systems"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/30 to-transparent" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-7xl mx-auto w-full px-5 md:px-10 pb-14 md:pb-24">
              <div className="max-w-3xl text-white">
                <div className="inline-block bg-black/50 backdrop-blur-sm px-3 py-1.5 mb-5">
                  <div className="overline" style={{ color: "#F8D16F" }}>
                    Modular wardrobes
                  </div>
                </div>
                <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight font-light">
                  Modular Wardrobes<br />
                  <span className="italic">Hinged &amp; Sliding Systems.</span>
                </h1>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="py-20 md:py-28 bg-ivory">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 reveal">
            <div className="overline mb-4">Overview</div>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight font-light">
              Two door systems.<br />
              <span className="italic text-teal">One quiet aesthetic.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 reveal space-y-5 text-lg md:text-xl text-black/75 leading-relaxed">
            <p>
              Every Tarush wardrobe is built around one of two systems — hinged
              or sliding — chosen to suit the room, the swing clearance, and how
              the wardrobe will be used every morning.
            </p>
            <p>
              Interiors are then customised to lifestyle: hanging heights, drawer
              counts, tie racks, shoe pull-outs, and LED profile lighting.
              Aesthetically the wardrobes stay quiet — floor-to-ceiling, scribed
              to the walls — so the room does the talking.
            </p>
          </div>
        </div>
      </section>

      {/* SYSTEM DIAGRAMS — Four ways to open a wardrobe */}
      <section
        data-testid="wardrobe-system-diagrams"
        className="bg-cream py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-16 reveal">
            <div className="max-w-2xl">
              <div className="overline mb-4">Configuration types</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light">
                Four ways<br />
                <span className="italic text-navy">to open a wardrobe.</span>
              </h2>
            </div>
            <p className="md:text-right md:max-w-sm text-black/60">
              We start by picking the door mechanism that fits your room — swing
              clearance, ceiling alignment, and the daily rhythm of getting
              dressed.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {WARDROBE_SYSTEM_DIAGRAMS.map((c, i) => (
              <div
                key={c.name}
                data-testid={`wardrobe-diagram-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="reveal group"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="aspect-[4/5] overflow-hidden bg-ivory border border-brandgray flex items-center justify-center p-8">
                  <svg
                    viewBox="0 0 100 75"
                    className="w-full h-full"
                    aria-hidden="true"
                  >
                    <rect
                      x="1"
                      y="1"
                      width="98"
                      height="73"
                      fill="none"
                      stroke="#E3DED2"
                      strokeWidth="0.4"
                      strokeDasharray="1 1"
                    />
                    {c.diagram.map((r, ri) => (
                      <rect
                        key={ri}
                        x={r.x}
                        y={r.y}
                        width={r.w}
                        height={r.h}
                        fill={r.accent ? "#2A6F73" : "#294890"}
                      />
                    ))}
                  </svg>
                </div>
                <div className="mt-5">
                  <div className="font-display text-2xl leading-tight text-navy">
                    {c.name}
                  </div>
                  <p className="mt-2 text-sm md:text-base text-black/70 leading-relaxed">
                    {c.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTERIOR HIGHLIGHTS */}
      <section
        data-testid="wardrobe-interior-highlights"
        className="bg-ivory py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-3xl mb-12 md:mb-16 reveal">
            <div className="overline mb-4">Interior configuration</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light">
              What's inside<br />
              <span className="italic text-teal">is planned first.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-black/70 leading-relaxed">
              Every wardrobe is designed around what actually lives inside — the
              hanging heights, drawer counts, and pull-outs are drawn up before
              the shutters are even discussed.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {WARDROBE_INTERIOR_HIGHLIGHTS.map((h, i) => (
              <div
                key={h.title}
                className="reveal bg-white border-l-4 border-teal p-6 md:p-7"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="overline mb-2">0{i + 1}</div>
                <div className="font-display text-xl md:text-2xl leading-tight text-navy">
                  {h.title}
                </div>
                <p className="mt-3 text-sm md:text-base text-black/70 leading-relaxed">
                  {h.detail}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MATERIALS & FINISHES */}
      <section
        data-testid="wardrobe-materials"
        className="bg-cream py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-3xl mb-12 md:mb-16 reveal">
            <div className="overline mb-4">Materials &amp; finishes</div>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight font-light">
              Choice of specifications<br />
              <span className="italic text-navy">as per project and budget.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-black/70 leading-relaxed">
              Every wardrobe can be built up or down in specification. Below are
              the primary options — we advise on the right combination during
              your dealer consultation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* Boards */}
            <div className="reveal bg-white border-t-4 border-teal p-8 md:p-10">
              <div className="overline mb-3">Board options</div>
              <div className="font-display text-2xl md:text-3xl leading-tight tracking-tight text-navy">
                Carcass ply
              </div>
              <ul className="mt-5 space-y-3 text-base text-black/80">
                {WARDROBE_MATERIALS.boards.map((b) => (
                  <li
                    key={b}
                    className="flex gap-3 items-center border-t border-brandgray pt-3"
                  >
                    <span className="w-1.5 h-1.5 bg-teal shrink-0" />
                    <span className="font-medium">{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Finishes */}
            <div
              className="reveal bg-white border-t-4 border-coral p-8 md:p-10"
              style={{ transitionDelay: "80ms" }}
            >
              <div className="overline mb-3">Shutter finishes</div>
              <div className="font-display text-2xl md:text-3xl leading-tight tracking-tight text-navy">
                Laminate &amp; Acrylic
              </div>
              <ul className="mt-5 space-y-3 text-base text-black/80">
                {WARDROBE_MATERIALS.finishes.map((f) => (
                  <li key={f.name} className="border-t border-brandgray pt-3">
                    <div className="font-medium">{f.name}</div>
                    <div className="text-sm text-teal mt-0.5">{f.specs}</div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Hardware */}
            <div
              className="reveal bg-white border-t-4 border-gold p-8 md:p-10"
              style={{ transitionDelay: "160ms" }}
            >
              <div className="overline mb-3">Hardware brands</div>
              <div className="font-display text-2xl md:text-3xl leading-tight tracking-tight text-navy">
                Soft-close, standard
              </div>
              <ul className="mt-5 space-y-3 text-base text-black/80">
                {WARDROBE_MATERIALS.hardware.map((h) => (
                  <li
                    key={h}
                    className="border-t border-brandgray pt-3 font-medium"
                  >
                    {h}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section
        data-testid="wardrobe-gallery-strip"
        className="bg-ivory py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="mb-10 md:mb-14 reveal">
            <div className="max-w-2xl">
              <div className="overline mb-4">Recent wardrobes</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light">
                Installed &amp;{" "}
                <span className="italic text-navy">in use.</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-8">
            {WARDROBE_GALLERY.map((src, i) => (
              <div
                key={i}
                className="reveal group bg-white p-2 md:p-3 shadow-[0_18px_40px_-18px_rgba(0,0,0,0.35)] ring-1 ring-navy/10 transition-transform duration-500 hover:-translate-y-1"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-cream">
                  <img
                    src={src}
                    alt={`Wardrobe project ${i + 1}`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                  />
                  <div className="absolute bottom-0 left-0 right-0 h-1/3 pointer-events-none bg-gradient-to-t from-black/45 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute left-3 bottom-3 text-white text-[10px] md:text-xs tracking-[0.24em] uppercase opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-500">
                    Project 0{i + 1}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Ready to design yours?"
        title="Get a Quotation."
        body="Share your room plan or requirement and our team will respond with a scope, timeline, and indicative pricing."
        primaryLabel="Get a Quotation"
        primaryTo="/contact"
        secondaryLabel="Become a Dealer"
        secondaryTo="/become-a-dealer"
        testid="wardrobe-cta"
      />
    </div>
  );
};

export default WardrobePage;
